import Box from "@mui/material/Box";
import React, { Suspense } from "react";
import type { JSX } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { PATHS, relativeTo } from "./analytics/routes";
import { AnalyticsMiddleware, AuthMiddleware, QualtricsMiddleware } from "./middleware";
import ProgressRing from "./ui-components/ProgressRing/ProgressRing";
import AuthHandler from "./views/Auth/AuthHandler";
import Landing from "./views/Public/Landing";

const Home = React.lazy(() => import("./views/Home/Home"));
const OrganizationView = React.lazy(() => import("./views/OrganizationView/OrganizationView"));
const GroupView = React.lazy(() => import("./views/GroupView/GroupView"));
const EditorView = React.lazy(() => import("./views/EditorView/EditorView"));
const SearchView = React.lazy(() => import("./views/Search/SearchView"));
const StandaloneView = React.lazy(() => import("./views/StandaloneView/StandaloneView"));
const NotFound = React.lazy(() => import("./views/Public/NotFound"));
const CreateGroupView = React.lazy(() => import("./views/CreateGroupView/CreateGroupView"));

/**
 * This is a full-screen component meant as a placeholder element before
 * lazy-loaded components are fully downloaded for display. After a
 * lazy-loaded component is downloaded, the full-screen placeholder will
 * not be shown anymore when the same lazy-loaded component is requested.
 */
function FullScreenProgress(): JSX.Element {
    return (
        <Box sx={{ width: "100vw", height: "100vh" }}>
            <ProgressRing label="Loading..." />
        </Box>
    );
}

export default function SchemaRouter(): JSX.Element {
    return (
        <Router>
            <Suspense fallback={<FullScreenProgress />}>
                <Routes>
                    <Route path="/" element={<QualtricsMiddleware />}>
                        <Route element={<AnalyticsMiddleware />}>
                            <Route index element={<Landing />} />
                            <Route path={PATHS.OAUTH} element={<AuthHandler />} />
                            <Route element={<AuthMiddleware />}>
                                <Route path={PATHS.HOME} element={<Home />} />
                                <Route path={PATHS.ORGANIZATION} element={<OrganizationView />} />
                                <Route path={PATHS.CREATE_GROUP} element={<CreateGroupView />} />
                                <Route path={PATHS.GROUP_MEMBERS} element={<GroupView />} />
                                <Route path={PATHS.LIBRARY_MEMBERS} element={<GroupView />} />
                                <Route path={PATHS.GROUP} element={<GroupView />}>
                                    <Route path={relativeTo(PATHS.GROUP, PATHS.LIBRARY)} element={<GroupView />}>
                                        <Route
                                            path={relativeTo(PATHS.LIBRARY, PATHS.SCHEMA_IN_LIBRARY)}
                                            element={<GroupView />}
                                        />
                                    </Route>
                                </Route>
                                <Route path={PATHS.EDITOR} element={<EditorView />} />
                                <Route path={PATHS.EDITOR_SCHEMA} element={<EditorView />} />
                                <Route path={PATHS.SEARCH} element={<SearchView />} />
                            </Route>
                            <Route path={PATHS.SCHEMA} element={<StandaloneView />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Route>
                </Routes>
            </Suspense>
        </Router>
    );
}
